import { useAuth } from "@/context/AuthProvider";
import axios from "axios";
import { useState, useEffect, useCallback, useRef } from "react";
import toast from "react-hot-toast";

const useChatWebSocket = (token, targetUser, roomName) => {
  const [messages, setMessages] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const { user } = useAuth();
  const currentUser = user?.username || "";
  const socketRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isConnectingRef = useRef(false);

  // Fetch messages for the specified room
  const fetchSingleRoom = useCallback(async () => {
    if(!token) return
    try {
      const response = await axios.get(
        `https://92.113.27.167:4225/get-messages-room/syriashop/${roomName}`,
        {
          headers: { token },
        }
      );

      setMessages(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching messages:", error);
      // toast.error("Failed to fetch chat messages.");
    }
  }, [roomName, token]);

  // Establish WebSocket connection
  const connectWebSocket = useCallback(() => {
    if(!token) return
    if (isConnectingRef.current) return;

    isConnectingRef.current = true;

    if (socketRef.current) {
      socketRef.current.close();
    }

    const ws = new WebSocket(
      `wss://92.113.27.167:4225/ws/chat/${token}/syriashop`
    );

    ws.onopen = () => {
      setConnectionStatus("connected");
      reconnectAttemptsRef.current = 0;
      socketRef.current = ws;
      isConnectingRef.current = false;
      fetchSingleRoom();
    };

    ws.onmessage = (event) => {
      const parsedData = JSON.parse(event.data);

      // Check for duplicates before adding the message
      setMessages((prevMessages) => {
        return [...prevMessages, parsedData]; // Add the new message
      });
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
      setConnectionStatus("error");
      toast.error("WebSocket encountered an error.");
    };

    ws.onclose = () => {
      console.log("WebSocket connection closed");
      setConnectionStatus("disconnected");
      if (reconnectAttemptsRef.current < 5) {
        reconnectAttemptsRef.current += 1;
        console.log(
          `Reconnecting... (Attempt ${reconnectAttemptsRef.current})`
        );
      } else {
        console.error("Maximum reconnection attempts reached.");
        toast.error("Failed to reconnect to WebSocket.");
      }
      isConnectingRef.current = false;
    };
  }, [token, fetchSingleRoom]);

  useEffect(() => {
    connectWebSocket();
    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  }, [connectWebSocket, token, roomName]);

  // Send message through WebSocket
  const sendMessage = useCallback(
    (message) => {

      if (socketRef.current?.readyState === WebSocket.OPEN) {
        const messagePayload = {
          id: Date.now(), // Generate a temporary unique ID for the message
          sender: targetUser,
          creator: currentUser,
          message,
          timestamp: new Date().toISOString(), // Add a timestamp
        };


        // Optimistically update the UI
        setMessages((prevMessages) => [...prevMessages, messagePayload]);

        // Send the message to the server
        socketRef.current.send(JSON.stringify(messagePayload));
      } else {
        console.error(
          "WebSocket is not connected. ReadyState:",
          socketRef.current?.readyState
        );
        toast.error("Cannot send message: WebSocket is not connected.");
      }
    },
    [currentUser, targetUser]
  );

  useEffect(() => {
    fetchSingleRoom();
  }, [fetchSingleRoom]);

  return {
    messages,
    connectionStatus,
    sendMessage,
    setMessages,
  };
};

export default useChatWebSocket;
